import { forwardRef, type CSSProperties, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Visual refresh (post-migration, see Button.tsx): previously deliberately
 * thin, relying entirely on the legacy element-selector CSS in index.css for
 * border/padding/radius so a migrated field and a still-legacy one looked
 * identical during the transition. Now carries its own utility classes on
 * the PulseOS-referenced token set (border-input, bg-card, focus ring),
 * which correctly wins over the legacy rule (utilities sit above legacy in
 * the layer order — see theme.css) — a genuine visual change from the
 * un-migrated fields still on index.css alone, accepted as part of this
 * redesign pass rather than deferred again.
 *
 * `invalid` layers a danger-tinted border/ring on top, applied after the
 * base classes so it wins the border-color conflict via cn/twMerge.
 */
const baseClass =
  'w-full rounded-[var(--radius)] border border-input bg-card px-3 py-2 text-[13px] text-ink shadow-sm transition-colors ' +
  'placeholder:text-muted-foreground ' +
  'focus-visible:outline-none focus-visible:border-teal focus-visible:ring-[3px] focus-visible:ring-teal/25 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const invalidClass = 'border-danger focus-visible:border-danger focus-visible:ring-danger/15';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}
export const Input = forwardRef<HTMLInputElement, InputProps>(({ className, invalid, ...props }, ref) => (
  <input ref={ref} className={cn(baseClass, invalid && invalidClass, className)} {...props} />
));
Input.displayName = 'Input';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({ className, invalid, ...props }, ref) => (
  <textarea ref={ref} className={cn(baseClass, 'min-h-[60px] resize-y', invalid && invalidClass, className)} {...props} />
));
Textarea.displayName = 'Textarea';

/**
 * Native <select> wrapper — kept distinct from the Radix-based Select in
 * Select.tsx, which is for cases wanting a fully custom-styled dropdown
 * (searchable feel, rich option content). This one is for a plain short
 * list where the OS-native picker is fine, just styled to match.
 *
 * `appearance-none` plus a hand-drawn chevron: without it, the browser draws
 * its own native control chrome for the *closed* box (background, border,
 * and on some Windows/Chromium builds a muted system caption color for the
 * selected value's text) on top of these utility classes, so a value like
 * "unknown" can render low-contrast even though `text-ink` is correctly
 * applied underneath. Suppressing the native chrome entirely is what makes
 * the value text reliably render at full `text-ink` contrast everywhere.
 */
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}
// A Tailwind arbitrary-value class (bg-[url('data:...')]) can't safely carry
// raw spaces/quotes from an inline SVG — the class tokenizer splits on
// whitespace, so the chevron silently fails to parse AND, worse, twMerge
// then treats the broken fragment as another `bg-*` utility and drops the
// base class's `bg-card` for conflicting on the same property. An inline
// style sidesteps both problems and is exactly what note this component
// already reserves the `style` prop path for.
const chevronStyle: CSSProperties = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%23374151' stroke-width='1.5'%3E%3Cpath d='M5 7.5L10 12.5L15 7.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.65rem center',
  backgroundSize: '14px 14px',
  // Belt-and-suspenders on top of the `text-ink` utility class: some
  // Windows/Chromium builds render a closed <select>'s value text through
  // an internal UA-shadow node that doesn't reliably pick up an author
  // `color` set only via a class, even with appearance:none and a matching
  // computed style — the text still visually renders as a muted system
  // caption gray. An inline color (highest cascade specificity short of
  // `!important`) plus a bumped font-weight closes that gap regardless of
  // which of the two causes is in play, and costs nothing when it isn't.
  color: '#111827',
  WebkitTextFillColor: '#111827',
  fontWeight: 500,
  // Still visibly clipped top/bottom on some compact selects (h-7 usages in
  // particular) even with the color fix above — the browser's own line box
  // for the option text doesn't reliably fit inside a short, padded select,
  // so part of the glyph gets cut off rather than just rendering faint.
  // Flexing the box and forcing line-height:1 makes the text center itself
  // vertically against the actual box height instead of an oversized line
  // box, which is what was clipping it.
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
};
export const NativeSelect = forwardRef<HTMLSelectElement, SelectProps>(({ className, invalid, style, ...props }, ref) => (
  <select
    ref={ref}
    className={cn(baseClass, 'appearance-none cursor-pointer pr-8', invalid && invalidClass, className)}
    style={{ ...chevronStyle, ...style }}
    {...props}
  />
));
NativeSelect.displayName = 'NativeSelect';
